<template>
  <!-- Feature flag (idea-MDP-592): the CheckpointTypeBanner renders by default;
       set the flag to false to hide it. Wired into CheckpointEditFormV2 above the
       content tab strip. -->
  <div
    v-if="featureEnabled"
    class="checkpoint-type-banner"
    :class="[variantClass, className]"
    :data-type="typeSlug"
  >
    <span class="checkpoint-type-banner__icon" aria-hidden="true">
      <!-- Video with TOC / Video Tutorial / Video: grey video-thumbnail chip with
           a white play glyph (verbatim from the canonical Figma spec). -->
      <svg
        v-if="iconKind === 'play'"
        class="checkpoint-type-banner__glyph checkpoint-type-banner__glyph--play"
        width="48"
        height="28"
        viewBox="0 0 48 28"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect width="48" height="28" rx="6" fill="#E4E7EC" />
        <path d="M20.125 20.0909V9.1818L29.3977 14.6364L20.125 20.0909Z" fill="#FFFFFF" />
      </svg>

      <!-- Inspector Only: grey chip with a black person + alert glyph. -->
      <svg
        v-else-if="iconKind === 'inspector'"
        class="checkpoint-type-banner__glyph checkpoint-type-banner__glyph--inspector"
        width="48"
        height="28"
        viewBox="0 0 48 28"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect width="48" height="28" rx="6" fill="#E4E7EC" />
        <path
          opacity="0.4"
          d="M31 17L34 20L31 23M34 20L28 20"
          stroke="#000000"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M24 17.5H19.5C18.1044 17.5 17.4067 17.5 16.8389 17.6722C15.5605 18.06 14.56 19.0605 14.1722 20.3389C14 20.9067 14 21.6044 14 23M26.5 9.5C26.5 11.9853 24.4853 14 22 14C19.5147 14 17.5 11.9853 17.5 9.5C17.5 7.0147 19.5147 5 22 5C24.4853 5 26.5 7.0147 26.5 9.5Z"
          stroke="#000000"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>

      <!-- Article: white chip with border and a grey document-lines glyph. -->
      <svg
        v-else
        class="checkpoint-type-banner__glyph checkpoint-type-banner__glyph--document"
        width="48"
        height="28"
        viewBox="0 0 48 28"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect x="0.5" y="0.5" width="47" height="27" rx="5.5" fill="#FCFCFD" />
        <rect x="0.5" y="0.5" width="47" height="27" rx="5.5" stroke="#D0D5DD" />
        <path
          d="M17.2812 12.0312V10.875H25.0625V12.0312H17.2812ZM17.2812 17.5938V16.4453H25.0625V17.5938H17.2812ZM17.2812 14.8125V13.6562H25.0625V14.8125H17.2812Z"
          fill="#667085"
        />
      </svg>
    </span>

    <div class="checkpoint-type-banner__content">
      <p class="checkpoint-type-banner__title">{{ content.title }}</p>
      <p class="checkpoint-type-banner__body">{{ content.body }}</p>
    </div>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref } from "vue";
import { LD_FLAG_IDEA_MDP_592 } from "@components/constants/launchDarklyFlagKeys";

/**
 * ER-11433 — CheckpointTypeBanner.
 *
 * Read-only info banner shown in the Checkpoint editor that explains the currently
 * selected checkpoint learning type. Purely presentational: it swaps its leading
 * glyph, bold title sentence and supporting line from the `type` prop (default
 * "Video with TOC"). It never accepts input and never emits events.
 *
 * Visual states (copy and glyphs verbatim from the canonical Figma spec):
 *   - Video with TOC / Video Tutorial / Video → grey #E4E7EC thumbnail chip with a
 *     white play glyph
 *   - Inspector Only → grey #E4E7EC chip with a black person + alert glyph
 *   - Article → white #FCFCFD chip (1px #D0D5DD border) with a #667085 document
 *     lines glyph
 * Every state shares the same shell: #F9F5FF background, 1px #D0D5DD border,
 * #344054 title and #475467 supporting text.
 *
 * Shipped behind the linked idea flag (idea-MDP-592, Checkpoint > Inspections Tab
 * surface): renders by default; the flag being off hides the banner. Wired into
 * CheckpointEditFormV2 above the content tab strip on every microlearning type.
 */
const props = defineProps({
  /** Which learning-type explanation to show. */
  type: {
    type: String,
    default: "Video with TOC",
    validator: (v) =>
      ["Video with TOC", "Video Tutorial", "Inspector Only", "Article", "Video"].includes(v),
  },
  /** Optional class override applied to the outer container. */
  className: {
    type: String,
    default: "",
  },
});

const DEFAULT_TYPE = "Video with TOC";

const TYPE_CONTENT = {
  "Video with TOC": {
    title: "This is a checkpoint with Video Learning and Table of Contents",
    body: "Ideal for longer videos with sound that need to be easily navigated",
  },
  "Video Tutorial": {
    title: "This is a checkpoint with Video Learning in a tutorial format",
    body: "Ideal for short videos without sound that show each step as a continuous reel",
  },
  "Inspector Only": {
    title: "This is a checkpoint with inspection left at the discretion of the Inspector",
    body: "The inspector is expected to pass or fail based solely on their interpretation of the checkpoint criteria",
  },
  Article: {
    title: "This is a checkpoint with Learning as a Knowledge Article",
    body: "Ideal for detailed information with pictures",
  },
  Video: {
    title: "This is a checkpoint that will use Video for Learning",
    body: "The video will be processed and validated against checkpoint criteria and context",
  },
};

const proxy = getCurrentInstance()?.proxy;
const featureEnabled = ref(true);

const effectiveType = computed(() => (TYPE_CONTENT[props.type] ? props.type : DEFAULT_TYPE));

const content = computed(() => TYPE_CONTENT[effectiveType.value]);

const typeSlug = computed(() => String(effectiveType.value).toLowerCase().replace(/\s+/g, "-"));

const variantClass = computed(() => ({
  "checkpoint-type-banner--video-with-toc": effectiveType.value === "Video with TOC",
  "checkpoint-type-banner--video-tutorial": effectiveType.value === "Video Tutorial",
  "checkpoint-type-banner--inspector-only": effectiveType.value === "Inspector Only",
  "checkpoint-type-banner--article": effectiveType.value === "Article",
  "checkpoint-type-banner--video": effectiveType.value === "Video",
}));

const iconKind = computed(() => {
  if (effectiveType.value === "Inspector Only") return "inspector";
  if (effectiveType.value === "Article") return "document";
  return "play"; // Video with TOC, Video Tutorial and Video share the play glyph.
});

onMounted(async () => {
  try {
    featureEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_592, true)) !== false;
  } catch {
    // Kill-switch lookup failures never hide the banner.
    featureEnabled.value = true;
  }
});
</script>

<style scoped>
.checkpoint-type-banner {
  display: flex;
  align-items: center;
  gap: 16px;
  box-sizing: border-box;
  width: 100%;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #d0d5dd;
  background-color: #f9f5ff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  font-family: Inter, sans-serif;
  text-align: left;
}

.checkpoint-type-banner__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  box-sizing: border-box;
  width: 48px;
  height: 28px;
}

.checkpoint-type-banner__glyph {
  display: block;
}

.checkpoint-type-banner__content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.checkpoint-type-banner__title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #344054;
}

.checkpoint-type-banner__body {
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}
</style>
